import { useRef } from 'react';
import type { ReactNode } from 'react';
import { nextLaneIndex } from './board-layout';

/** A swipe shorter than this is a scroll or a tap that wandered, not a lane change. */
const SWIPE_THRESHOLD_PX = 56;
/** A gesture more vertical than horizontal is the reader scrolling the column, not swiping. */
const HORIZONTAL_DOMINANCE = 1.4;

/**
 * 🔴 THE PHONE BOARD: ONE LANE AT A TIME, WITH A SWIPE BETWEEN THEM.
 *
 * ⭐⭐ The other lanes are NOT RENDERED — not merely off-screen. A scroll-snap carousel would keep all
 * six in the DOM, which means a phone builds six lanes' worth of cards to show one, and a test
 * counting the columns on screen would find six and call it a pass. Rendering only the active lane is
 * both the honest thing to assert and the fast thing to do.
 *
 * ⚠️ The swipe is not the only way through: there are tabs, and chevrons with real accessible names.
 * A gesture nobody can discover is not an interface, and a keyboard or a screen reader cannot swipe.
 *
 * ⭐ ONE row of chrome, not two. The shell already puts a tab bar at the bottom of a phone screen; a
 * second bottom bar of my own would leave a 390×844 board with more chrome than board.
 */
export function LaneSwitcher({
  lanes,
  activeIndex,
  onChange,
  children,
}: {
  lanes: readonly { code: string; label: string }[];
  activeIndex: number;
  onChange: (code: string) => void;
  children: ReactNode;
}) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const active = lanes[activeIndex];

  const move = (direction: 1 | -1) => {
    const target = lanes[nextLaneIndex(activeIndex, lanes.length, direction)];
    if (target) onChange(target.code);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 border-b border-[color:var(--color-line)] px-1 py-1.5">
        <button
          type="button"
          onClick={() => move(-1)}
          aria-label="Previous lane"
          className="h-8 shrink-0 rounded px-2 text-sm text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          ‹
        </button>

        <div role="tablist" aria-label="Board lanes" className="flex flex-1 gap-1 overflow-x-auto">
          {lanes.map((lane, index) => (
            <button
              key={lane.code}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              onClick={() => onChange(lane.code)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap ${
                index === activeIndex
                  ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                  : 'bg-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
              }`}
            >
              {lane.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => move(1)}
          aria-label="Next lane"
          className="h-8 shrink-0 rounded px-2 text-sm text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
        >
          ›
        </button>
      </div>

      <div
        role="tabpanel"
        aria-label={
          active
            ? `${active.label} lane — lane ${activeIndex + 1} of ${lanes.length}, swipe to change`
            : 'Board'
        }
        onTouchStart={(event) => {
          const touch = event.touches[0];
          start.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
        }}
        onTouchEnd={(event) => {
          const from = start.current;
          const touch = event.changedTouches[0];
          start.current = null;
          if (!from || !touch) return;

          const dx = touch.clientX - from.x;
          const dy = touch.clientY - from.y;
          if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
          // ⚠️ A mostly-vertical gesture is the reader scrolling a column. Treating it as a swipe
          // would make the board change lane while somebody was reading down one.
          if (Math.abs(dx) < Math.abs(dy) * HORIZONTAL_DOMINANCE) return;

          move(dx < 0 ? 1 : -1);
        }}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {children}
      </div>
    </div>
  );
}
